/**
 * RelayProgressCard
 *
 * Shows a team's relay journey progress:
 * - Completion bar
 * - Current city + next city
 * - Effective vs raw distance
 * - Eligibility badges
 *
 * Props:
 *   journey  - from GET /campaigns/:id/teams/:teamId/relay  (.data)
 *   compact  - boolean, show condensed version
 */
import RelayTeamsMap from '../map/RelayTeamsMap';

function EligibilityBadge({ isChampion, isFinisher }) {
  if (isFinisher) {
    return (
      <span style={{ ...badgeStyle, background: '#f6ffed', color: '#52c41a', border: '1px solid #b7eb8f' }}>
        🏅 Finisher
      </span>
    );
  }
  if (!isChampion) {
    return (
      <span style={{ ...badgeStyle, background: '#fff1f0', color: '#cf1322', border: '1px solid #ffa39e' }}>
        ⚠️ Dropout (mất cơ hội vô địch)
      </span>
    );
  }
  return (
    <span style={{ ...badgeStyle, background: '#e6f4ff', color: '#1677ff', border: '1px solid #91caff' }}>
      🏆 Champion eligible
    </span>
  );
}

const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 10px',
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 500,
};

export default function RelayProgressCard({ journey, compact = false, showMap = true }) {
  if (!journey) return null;

  const {
    team_name,
    team_logo,
    completion_percent = 0,
    effective_distance_km = 0,
    target_km = 15000,
    position = {},
    route = [],
    is_champion_eligible,
    is_finisher_eligible,
    last_computed_at,
  } = journey;

  const distanceLeft = Math.max(0, target_km - effective_distance_km);

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 16,
        padding: compact ? '12px 16px' : '20px 24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
        marginBottom: 16,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        {team_logo && (
          <img
            src={team_logo}
            alt=""
            style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
          />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{team_name || 'Team'}</div>
          <EligibilityBadge isChampion={is_champion_eligible} isFinisher={is_finisher_eligible} />
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1677ff' }}>
            {completion_percent.toFixed(1)}%
          </div>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>hoàn thành</div>
        </div>
      </div>

      {/* Relay map — shown only in standalone mode (showMap=true).
          When used inside RelayLeaderboard, showMap=false because
          the shared map above the table already handles all teams. */}
      {!compact && showMap && route.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <RelayTeamsMap
            teams={[{
              team_id:            team_name,
              team_name,
              team_logo,
              effective_distance: effective_distance_km * 1000,
              completion_percent,
              is_finisher_eligible,
              is_frozen:          journey.is_frozen,
            }]}
            route={route}
            targetKm={target_km}
            height={260}
          />
        </div>
      )}

      {/* Progress bar (compact fallback) */}
      {compact && (
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              height: 8,
              background: '#f0f0f0',
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(100, completion_percent)}%`,
                height: '100%',
                background: is_finisher_eligible ? '#52c41a' : '#1677ff',
                borderRadius: 4,
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </div>
      )}

      {/* Stats row */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
          fontSize: 13,
        }}
      >
        <StatItem
          label="Đã chạy (hiệu quả)"
          value={`${effective_distance_km.toFixed(1)} km`}
          color="#1677ff"
        />
        <StatItem
          label="Còn lại"
          value={`${distanceLeft.toFixed(1)} km`}
          color="#8c8c8c"
        />
        {position.currentCityName && (
          <StatItem
            label="Đang ở"
            value={`${position.currentCityEmoji} ${position.currentCityName}`}
          />
        )}
        {position.nextCityName && (
          <StatItem
            label="Chặng tiếp theo"
            value={`${position.nextCityEmoji} ${position.nextCityName} (còn ${position.distanceToNextCityKm?.toFixed(0)} km)`}
          />
        )}
      </div>

      {last_computed_at && !compact && (
        <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 12 }}>
          Cập nhật lần cuối: {new Date(last_computed_at).toLocaleString('vi-VN')}
        </div>
      )}
    </div>
  );
}

function StatItem({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 600, color: color || '#262626' }}>{value}</div>
    </div>
  );
}
